import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { SaveFiltersModal } from "./SaveFiltersModal";
import ConfirmationModal from "../modal/ConfirmationModal";

export const FilterPresets = ({ 
  filterPresets = [],
  selectedPreset = null,
  presetsLoading = false,
  presetsError = null,
  onLoadPresets,
  onApplyPreset,
  onSavePreset,
  onDeletePreset,
  currentFilterQuery = null,
  currentCars = [],
  currentSubsystems = [],
  currentEvents = [],
  currentConsist = null,
  isQueryValid = false
}) => {
  const [showPresetsDropdown, setShowPresetsDropdown] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [presetSearchTerm, setPresetSearchTerm] = useState('');
  const [newPresetName, setNewPresetName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  const [expandedPresets, setExpandedPresets] = useState(new Set());
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmPreset, setConfirmPreset] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Access Angular filter for consistent date formatting
  const injector = angular.element(".ng-scope").injector();
  const formatTimestamp = injector.get('formatTimestampFilter');

  // Ensure filterPresets is always an array
  const presetsArray = Array.isArray(filterPresets) ? filterPresets : [];

  // Load presets when dropdown is first opened
  useEffect(() => {
    if (showPresetsDropdown && presetsArray.length === 0 && !presetsLoading) {
      onLoadPresets?.();
    }
  }, [showPresetsDropdown]);

  const togglePresetsDropdown = () => {
    setShowPresetsDropdown(!showPresetsDropdown);
    if (!showPresetsDropdown) {
      setPresetSearchTerm('');
    }
  };

  const handleApplyPreset = (preset) => {
    // Toggle: if clicking already-selected preset, deselect it
    if (selectedPreset && selectedPreset.id === preset.id) {
      onApplyPreset?.(null); // Pass null to clear
    } else {
      onApplyPreset?.(preset);
    }
    setShowPresetsDropdown(false);
  };

  const handleDeletePreset = (e, preset) => {
    e.stopPropagation();
    setConfirmPreset(preset);
    setShowConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (confirmPreset) {
      onDeletePreset?.(confirmPreset);
    }
    setShowConfirm(false);
    setConfirmPreset(null);
  };

  const handleCancelDelete = () => {
    setShowConfirm(false);
    setConfirmPreset(null);
  };

  const openSaveDialog = () => {
    setShowSaveDialog(true);
    setNewPresetName('');
  };

  const closeSaveDialog = () => {
    setShowSaveDialog(false);
    setNewPresetName('');
  };

  const hasFilterQuery = () => {
    if (!isQueryValid || !currentFilterQuery) {
      return false;
    }

    if (typeof currentFilterQuery === 'string') {
      return currentFilterQuery.trim().length > 0;
    }

    if (typeof currentFilterQuery === 'object') {
      if (Array.isArray(currentFilterQuery.rules)) {
        return currentFilterQuery.rules.length > 0;
      }
      return Object.keys(currentFilterQuery).length > 0;
    }

    return false;
  };

  const hasSelectedFilters = () => Boolean(
    hasFilterQuery() ||
    (currentCars && currentCars.length > 0) ||
    (currentSubsystems && currentSubsystems.length > 0) ||
    (currentEvents && currentEvents.length > 0) ||
    currentConsist
  );

  const validFilterQuery = hasFilterQuery() ? currentFilterQuery : null;

  const handleSavePreset = async () => {
    setSavingPreset(true);
    try {
      const preset = {
        name: newPresetName.trim(),
        environmentDataQuery: validFilterQuery,
        eventIds: currentEvents && currentEvents.length > 0 ? currentEvents.map(event => event.value) : null,
        cars: currentCars && currentCars.length > 0 ? currentCars : null,
        subsystemIds: currentSubsystems && currentSubsystems.length > 0 ? currentSubsystems.map(subsystem => subsystem.id) : null,
        consists: currentConsist ? [currentConsist.value] : null
      };

      await onSavePreset(preset);
      closeSaveDialog();
    } catch (error) {
      console.error('Error saving preset:', error);
    } finally {
      setSavingPreset(false);
    }
  };

  const filteredPresets = presetsArray.filter(preset => 
    preset.name.toLowerCase().includes(presetSearchTerm.toLowerCase())
  );

  const toggleExpanded = (presetId, e) => {
    e.stopPropagation();
    setExpandedPresets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(presetId)) {
        newSet.delete(presetId);
      } else {
        newSet.add(presetId);
      }
      return newSet;
    });
  };

  const isNameTruncated = (name) => {
    return name && name.length > 40;
  };

  const getDisplayName = (preset) => {
    if (!isNameTruncated(preset.name)) {
      return preset.name;
    }
    if (expandedPresets.has(preset.id)) {
      return preset.name;
    }
    return preset.name.substring(0, 40) + '...';
  };

  // Close dropdown if clicking outside
  useEffect(() => {
    if (!showPresetsDropdown) return;
    function handleClickOutside(event) {
      const refs = [dropdownRef.current, buttonRef.current];
      if (!refs.some(ref => ref?.contains(event.target))) {
        setShowPresetsDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPresetsDropdown]);

  return (
    <>
      <div className="persistOptControllerContainer" data-testid="filter-presets-container">
        {/* Saved Presets Dropdown Button */}
        <div className="filterPresetsButtonContainer">
          <div className="chartControlTitle">Presets</div>
          <div className="presetDropdownWrapper">
            <button
              type="button"
              className="btn btn-primary presets"
              onClick={togglePresetsDropdown}
              title="Select a saved filter preset"
              ref={buttonRef}
              data-testid="filter-presets-dropdown-toggle"
            >
              <i className="fas fa-bookmark"></i>
            </button>

            {/* Dropdown Menu */}
            {showPresetsDropdown && (
              <div className="savedFiltersDropdownMenu" ref={dropdownRef} data-testid="filter-presets-dropdown-menu">
                <div className="presetSearchBox">
                  <input
                    type="text"
                    className="rule rule-fields"
                    placeholder="Search presets..."
                    value={presetSearchTerm}
                    onChange={(e) => setPresetSearchTerm(e.target.value)}
                    data-testid="filter-presets-search-input"
                  />
                </div>
                <div className="presetsList">
                  {presetsLoading && (
                    <div className="presetLoadingSpinner">
                      <i className="fas fa-spinner fa-pulse"></i> Loading...
                    </div>
                  )}
                  {presetsError && (
                    <div className="presetError">
                      <i className="fas fa-exclamation-triangle"></i> {presetsError}
                    </div>
                  )}
                  {!presetsLoading && !presetsError && filteredPresets.length === 0 && (
                    <div className="presetNoResults">
                      No presets {presetSearchTerm ? 'found' : 'saved yet'}
                    </div>
                  )}
                  {!presetsLoading && filteredPresets.map((preset, index) => (
                    <button
                      key={preset.id}
                      className={`presetItem ${selectedPreset && selectedPreset.id === preset.id ? 'active' : ''}`}
                      onClick={() => handleApplyPreset(preset)}
                      type="button"
                      data-testid={`filter-preset-item-${index}`}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="presetName">
                          {getDisplayName(preset)}
                          {isNameTruncated(preset.name) && (
                            <button
                              type="button"
                              className="presetShowMore"
                              onClick={(e) => toggleExpanded(preset.id, e)}
                              title={expandedPresets.has(preset.id) ? 'Show less' : 'Show more'}
                              data-testid={`filter-preset-show-more-${index}`}
                            >
                              {expandedPresets.has(preset.id) ? ' less' : ' more'}
                            </button>
                          )}
                        </div>
                        {preset.creationDate && (
                          <div className="presetCreationDate">
                            created: {formatTimestamp(preset.creationDate)}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="presetDelete"
                        onClick={(e) => handleDeletePreset(e, preset)}
                        title="Delete this preset"
                        data-testid={`filter-preset-delete-${index}`}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Save Current Button */}
        <div className="saveCurrentButtonContainer">
          <div className="chartControlTitle">Save Preset</div>
          <button
            type="button"
            className="btn btn-primary save-presets"
            onClick={openSaveDialog}
            title={hasSelectedFilters() ? "Save current filters as preset" : "Select at least one filter before saving"}
            data-testid="filter-presets-save-current-button"
            disabled={!hasSelectedFilters()}
          >
            <i className="fas fa-save"></i>
          </button>
        </div>
      </div>

      {/* Save Preset Modal Dialog */}
      <SaveFiltersModal
        isOpen={showSaveDialog}
        onClose={closeSaveDialog}
        onSave={handleSavePreset}
        presetName={newPresetName}
        onPresetNameChange={setNewPresetName}
        currentFilterQuery={validFilterQuery}
        currentCars={currentCars}
        currentSubsystems={currentSubsystems}
        currentEvents={currentEvents}
        currentConsist={currentConsist}
        isSaving={savingPreset}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirm}
        message={confirmPreset ? `Are you sure you want to delete the saved filter "${confirmPreset.name}"?` : ''}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};

FilterPresets.propTypes = {
  filterPresets: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  selectedPreset: PropTypes.object,
  presetsLoading: PropTypes.bool,
  presetsError: PropTypes.string,
  onLoadPresets: PropTypes.func,
  onApplyPreset: PropTypes.func,
  onSavePreset: PropTypes.func,
  onDeletePreset: PropTypes.func,
  currentFilterQuery: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  currentCars: PropTypes.array,
  currentSubsystems: PropTypes.array,
  currentEvents: PropTypes.array,
  currentConsist: PropTypes.object,
  isQueryValid: PropTypes.bool
};
